/**
 * Shared recommendations and enforced upload limits for sales images.
 * @module
 */
import { FILE_SIZES } from "common/constants";

/** 3:2 — the frame Showroom crops option cards to, so a recommended image is never cropped. */
export const SALES_IMAGE_RECOMMENDED_WIDTH = 1620;
export const SALES_IMAGE_RECOMMENDED_HEIGHT = 1080;

export const SALES_IMAGE_MIN_SHORT_EDGE = 720;

export const SALES_IMAGE_MIN_ASPECT_RATIO = 0.5;
export const SALES_IMAGE_MAX_ASPECT_RATIO = 2;

export const SALES_IMAGE_MAX_FILE_SIZE = FILE_SIZES.MB * 5;

const RECOMMENDED_SIZE = `${SALES_IMAGE_RECOMMENDED_WIDTH} × ${SALES_IMAGE_RECOMMENDED_HEIGHT} px`;

/** Formats width ÷ height as `w:h`, inverting below 1 so 0.5 reads as "1:2". */
function formatAspectRatio(ratio: number): string {
  const trimmed = (value: number) => Number(value.toFixed(2)).toString();
  if (ratio >= 1) {
    return `${trimmed(ratio)}:1`;
  }
  return `1:${trimmed(1 / ratio)}`;
}

export const SALES_IMAGE_ASPECT_RATIO_RANGE = `${formatAspectRatio(
  SALES_IMAGE_MIN_ASPECT_RATIO,
)} and ${formatAspectRatio(SALES_IMAGE_MAX_ASPECT_RATIO)}`;

export const SALES_IMAGE_RECOMMENDATION = `Images are recommended to have an aspect ratio between ${SALES_IMAGE_ASPECT_RATIO_RANGE}, and a minimum width and height of ${SALES_IMAGE_MIN_SHORT_EDGE} px.`;

export const SALES_IMAGE_UPLOAD_HINT = `Recommended ${RECOMMENDED_SIZE} or larger.
${SALES_IMAGE_RECOMMENDATION} Must be PNG or JPEG, under ${SALES_IMAGE_MAX_FILE_SIZE / FILE_SIZES.MB} MB.`;

export const SALES_IMAGE_UPLOAD_TOOLTIP = `Option cards crop to a 3:2 landscape frame, so 3:2 images show in full. Portrait and square images are accepted and show uncropped in the detail view. Images under ${SALES_IMAGE_MIN_SHORT_EDGE} px on either side may look poor when a buyer opens them full screen.`;

export interface SalesImageDimensions {
  width: number;
  height: number;
}

/** @returns A warning naming the measured size and recommendations, or null when the image meets them. */
export function validateSalesImageDimensions({ width, height }: SalesImageDimensions): string | null {
  if (!width || !height) {
    return "Could not read the image dimensions. Use a PNG or JPEG image.";
  }

  const aspectRatio = width / height;
  const isUsable =
    Math.min(width, height) >= SALES_IMAGE_MIN_SHORT_EDGE &&
    aspectRatio >= SALES_IMAGE_MIN_ASPECT_RATIO &&
    aspectRatio <= SALES_IMAGE_MAX_ASPECT_RATIO;

  if (isUsable) {
    return null;
  }

  return `The image size of ${width} × ${height} px may render incorrectly in Showroom. ${SALES_IMAGE_RECOMMENDATION}`;
}

export const SALES_IMAGE_UPLOAD_OPTIONS = {
  sizeLimit: SALES_IMAGE_MAX_FILE_SIZE,
  imageTypes: ["png", "jpeg"] as const,
};
